// Rich, SEO-oriented content for the standalone marketing pages.
// Keyed by full route path.

export const PAGES = {
  "/admission/preparing-for-your-stay": {
    eyebrow: "Admission",
    title: "Preparing for Your Stay",
    intro:
      "Taking the first step into inpatient treatment is brave. Here's exactly what to expect and how to prepare, so you can arrive at New Beginnings Recovery feeling calm, informed and ready to heal.",
    image: "https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=compress&w=1600&q=80",
    sections: [
      {
        heading: "A simple, supportive intake process",
        body: [
          "Admission begins the moment you call us at 1-647-482-6308. Our admissions team will listen without judgement, answer your questions and complete a brief, confidential assessment to understand your needs.",
          "Thanks to immediate admittance and shorter wait lists, many clients can begin treatment the same day. We'll confirm your private-pay options or insurance coverage (including GreenShield) and walk you through every detail before you arrive.",
        ],
      },
      {
        heading: "What to bring",
        body: [
          "Pack comfortably for a four-week residential stay near the Scarborough Bluffs. We recommend a week's worth of comfortable clothing, personal toiletries, any prescribed medications in their original packaging, and a few items that bring you comfort.",
        ],
      },
    ],
    bullets: {
      heading: "A calm arrival checklist",
      items: [
        "Comfortable clothing for one week (laundry is available)",
        "Prescribed medications in original, labelled containers",
        "Health card and insurance details (e.g. GreenShield), if applying through benefits",
        "A journal, photos or comfort items",
        "An open mind and willingness to begin",
      ],
    },
  },

  "/admission/during-your-stay": {
    eyebrow: "Admission",
    title: "During Your Stay",
    intro:
      "Your recovery journey unfolds over a comprehensive four-week inpatient program, with round-the-clock supervision, on-site treatment and a daily rhythm designed to rebuild your life from the inside out.",
    image: "https://images.pexels.com/photos/19473771/pexels-photo-19473771.jpeg?auto=compress&w=1600&q=80",
    sections: [
      {
        heading: "Round-the-clock care in a real home",
        body: [
          "During your stay you'll benefit from 24/7 supervision and access to on-site treatment options. Our dedicated staff is committed to your comfort, and our counsellors are always available to support and guide you through each step of recovery.",
          "We understand health is a priority, so doctor appointments and check-ups are accommodated throughout your stay.",
        ],
      },
      {
        heading: "Real-world practice from day one",
        body: [
          "Unlike other centres, we bring real life into your recovery immediately. You don't just learn skills inside a bubble — you apply them in real life: grocery shopping, banking and attending meetings, so each step is grounded in the world you'll return to.",
        ],
      },
    ],
    bullets: {
      heading: "A typical day includes",
      items: [
        "Individual therapy and group-based treatment",
        "Evidence-based sessions (CBT, DBT, SMART Recovery)",
        "Mindfulness, meditation and life-skills coaching",
        "Nutritious meals and time outdoors",
        "12-step work and community connection",
      ],
    },
  },

  "/admission/financing-your-stay": {
    eyebrow: "Admission",
    title: "Financing Your Stay",
    intro:
      "Cost should never stand between you and recovery. We accept private pay and most insurance plans, including GreenShield, and our team will help you understand exactly what your treatment involves.",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=compress&w=1600&q=80",
    sections: [
      {
        heading: "Coverage made simple",
        body: [
          "Many of our clients pay privately or access treatment through insurance benefits such as GreenShield. During admission, our team will help verify your coverage and explain any details, so there are no surprises.",
          "If you're unsure about your coverage, call us at 1-647-482-6308 — we'll help you find the clearest path forward.",
        ],
      },
      {
        heading: "An investment in a whole new life",
        body: [
          "Your four-week inpatient program includes accommodation, meals, medical supervision, individual and group therapy, and lifetime aftercare support. We'll work with your lifestyle, work commitments and loved ones to make treatment achievable.",
        ],
      },
    ],
    bullets: {
      heading: "What's included",
      items: [
        "Residential accommodation near the Scarborough Bluffs",
        "24/7 supervision with on-call medical care",
        "Individual and group evidence-based therapy",
        "Family Care Program involvement",
        "Lifetime aftercare and alumni support",
      ],
    },
  },

  "/about/what-we-treat": {
    eyebrow: "About Us",
    title: "What We Treat",
    intro:
      "New Beginnings Recovery Home provides integrated treatment for drug and alcohol addiction and co-occurring mental health disorders — caring for the whole person, not just the symptoms.",
    image: "https://images.unsplash.com/photo-1544027993-37dbfe43562a?auto=compress&w=1600&q=80",
    sections: [
      {
        heading: "Addiction & co-occurring mental health",
        body: [
          "We treat drug and alcohol addiction alongside co-occurring mental health disorders such as PTSD, depression and anxiety. Our licensed, accredited, multidisciplinary team delivers both group-based treatment and individual therapy tailored to your needs.",
          "As an addiction treatment rehab centre, New Beginnings is distinguished by extensive expertise and unwavering dedication to top-quality patient care. We recognize that addiction touches every aspect of a person's life.",
        ],
      },
      {
        heading: "A holistic, biopsychosocial approach",
        body: [
          "We advocate for holistic rehab that addresses the psychological, physical, social and spiritual dimensions of well-being. This is embodied in our inter-professional team of counsellors, doctors, occupational therapists, nutritionists, family therapists and recreational professionals.",
        ],
      },
    ],
    bullets: {
      heading: "Conditions & concerns we support",
      items: [
        "Alcohol addiction and dependence",
        "Drug and substance addiction",
        "Co-occurring PTSD, depression and anxiety",
        "Relapse prevention and early recovery",
        "Life-skills, family and community reintegration",
      ],
    },
  },

  "/about/get-involved": {
    eyebrow: "About Us",
    title: "Get Involved",
    intro:
      "Recovery is a community. Whether you're an alum, a family member, a professional partner or a supporter, there are meaningful ways to help others find their new beginning.",
    image: "https://images.unsplash.com/photo-1520880867055-1e30d1cb001c?auto=compress&w=1600&q=80",
    sections: [
      {
        heading: "Ways to give back",
        body: [
          "Our alumni community is a powerful source of hope. Share your story, mentor someone in early recovery, or join alumni events that keep the connection strong long after treatment ends.",
          "Families and community partners can help too — by learning about addiction, reducing stigma, and referring those who need support.",
        ],
      },
      {
        heading: "Partner with us",
        body: [
          "We welcome partnerships with unions, employers, healthcare providers and community organizations who share our commitment to accessible, compassionate recovery care.",
        ],
      },
    ],
    bullets: {
      heading: "Get involved by",
      items: [
        "Joining our alumni network and events",
        "Volunteering or sharing your recovery story",
        "Referring a loved one, client or colleague",
        "Partnering as a union, employer or provider",
        "Donating to support those who need care",
      ],
    },
  },

  "/programs/inpatient-treatment": {
    eyebrow: "Programs",
    title: "Inpatient Treatment",
    intro:
      "Our residential inpatient program is the heart of New Beginnings Recovery — an immersive, four-week environment offering round-the-clock care and continuous medical assistance for drug and alcohol addiction.",
    image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=compress&w=1600&q=80",
    sections: [
      {
        heading: "An immersive residential experience",
        body: [
          "Our residential facility offers daily programming for those struggling with drug and alcohol addiction. Your journey spans four weeks and is tailored to prepare you for a successful reintegration into everyday life — equipped with the tools to lead a happy, joyous and free lifestyle.",
          "You'll benefit from 24/7 supervision, on-site treatment and a dedicated team of counsellors ready to guide you through every step of recovery.",
        ],
      },
      {
        heading: "Evidence-based programming",
        body: [
          "We deliver proven, evidence-based treatment models for addiction and related mental health disorders — including CBT, DBT and SMART Recovery — integrated with the 12 steps of recovery and a strengths-based philosophy.",
        ],
      },
    ],
    bullets: {
      heading: "Program highlights",
      items: [
        "Four-week residential (28-day) program",
        "24/7 supervision and on-call medical care",
        "Individual therapy and group treatment",
        "CBT, DBT, SMART Recovery & mindfulness",
        "Seamless transition into aftercare",
      ],
    },
  },

  "/programs/aftercare": {
    eyebrow: "Programs",
    title: "Aftercare Options",
    intro:
      "We're not just here for 28 days — we walk with you for a lifetime. Our aftercare emphasizes ongoing support to help you maintain recovery long after treatment ends.",
    image: "https://images.unsplash.com/photo-1501183638710-841dd1904471?auto=compress&w=1600&q=80",
    sections: [
      {
        heading: "Support that continues",
        body: [
          "Our counsellors remain accessible after treatment to address any recovery-related issues that arise. We provide valuable resources to empower you to sustain your progress on the journey of recovery.",
          "With ongoing aftercare, family involvement and the same trusted team, you stay supported every step of the way.",
        ],
      },
      {
        heading: "Rooted in the recovery community",
        body: [
          "Beyond our treatment plans and therapy, we connect you with the extensive recovery community and the 12 steps of recovery — so you're never walking alone.",
        ],
      },
    ],
    bullets: {
      heading: "Aftercare includes",
      items: [
        "Ongoing access to your counselling team",
        "Alumni community and events",
        "Relapse-prevention resources",
        "Family involvement and support",
        "Connection to 12-step and recovery communities",
      ],
    },
  },

  "/programs/family-care": {
    eyebrow: "Programs",
    title: "Family Care",
    intro:
      "Addiction affects the whole family — and so does recovery. Our Introduction to Family Care Program educates and supports loved ones so families can heal together.",
    image: "https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=compress&w=1600&q=80",
    sections: [
      {
        heading: "Healing together",
        body: [
          "New Beginnings Recovery's Introduction to Family Care Program aims to educate family members about addiction as a health condition, and to support them through their own healing.",
          "Research consistently shows that clients whose families are engaged have lower relapse rates and stronger support systems when they return home.",
        ],
      },
      {
        heading: "Rebuilding trust and communication",
        body: [
          "We help families learn healthy communication, set supportive boundaries and rebuild trust — turning recovery into something you build together, not alone.",
        ],
      },
    ],
    bullets: {
      heading: "The Family Care Program offers",
      items: [
        "Education about addiction and mental health",
        "Guidance on healthy communication",
        "Support for setting loving boundaries",
        "A path to rebuilding trust",
        "Involvement throughout treatment and aftercare",
      ],
    },
  },
};
